// @spec SHL-STR-016

import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'quantifier-recent-searches';
const MAX_RECENT_SEARCHES = 10;

export interface UseRecentSearchesReturn {
  /** List of recent search terms */
  recentSearches: string[];
  /** Add a search to history */
  addSearch: (term: string) => void;
  /** Remove a specific search */
  removeSearch: (term: string) => void;
  /** Clear all recent searches */
  clearAll: () => void;
}

/**
 * Hook for managing recent searches in localStorage.
 *
 * Stores up to 10 recent searches, deduplicates entries
 * (moving repeated searches to top), and trims whitespace.
 *
 * @example
 * const { recentSearches, addSearch, removeSearch, clearAll } = useRecentSearches();
 *
 * // Add a search when user submits
 * addSearch('compliance report');
 *
 * // Remove a specific search
 * removeSearch('old search');
 *
 * // Clear all history
 * clearAll();
 */
export function useRecentSearches(): UseRecentSearchesReturn {
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed: unknown = JSON.parse(stored);
        if (
          Array.isArray(parsed) &&
          parsed.every((item): item is string => typeof item === 'string')
        ) {
          setRecentSearches(parsed);
        }
      }
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  // Persist to localStorage whenever searches change
  const persistSearches = useCallback((searches: string[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(searches));
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  const addSearch = useCallback(
    (term: string) => {
      const trimmed = term.trim();
      if (!trimmed) return;

      setRecentSearches((prev) => {
        // Remove if already exists (will be re-added at top)
        const filtered = prev.filter((s) => s.toLowerCase() !== trimmed.toLowerCase());
        // Add to beginning and limit to max
        const updated = [trimmed, ...filtered].slice(0, MAX_RECENT_SEARCHES);
        persistSearches(updated);
        return updated;
      });
    },
    [persistSearches]
  );

  const removeSearch = useCallback(
    (term: string) => {
      setRecentSearches((prev) => {
        const updated = prev.filter((s) => s !== term);
        persistSearches(updated);
        return updated;
      });
    },
    [persistSearches]
  );

  const clearAll = useCallback(() => {
    setRecentSearches([]);
    persistSearches([]);
  }, [persistSearches]);

  return {
    recentSearches,
    addSearch,
    removeSearch,
    clearAll,
  };
}
